import { resolvePaymentQr } from './paymentQr';
import { VIEW, maskPhoneTail, sanitizeView } from './protocol';

// Framework-independent rules that turn "what the document currently says"
// into the customer-facing view. The Vue 2 form -> input mapping lives in
// src/legacy/customerDisplay (temporary); this file survives the migration.

function digitsOf(value) {
  return String(value === null || value === undefined ? '' : value).replace(/\D/g, '');
}

function cents(value) {
  return Math.round((Number(value) || 0) * 100);
}

// Everything that can change what the customer would pay. Editing any of it
// after a save invalidates the QR until the next successful save.
export function paymentFingerprint(items, totals) {
  return JSON.stringify([
    cents(totals.subtotal),
    cents(totals.discount),
    cents(totals.vat),
    cents(totals.net),
    (items || []).map((item) => [String(item.name || ''), cents(item.price), cents(item.discount)])
  ]);
}

// Remembers what the last SUCCESSFUL save (or the loaded saved document)
// contained: a fingerprint of its payment-affecting data and the payment QR for
// that amount. The customer-facing total/QR are only shown while the live
// document still matches that saved state (isInSync / resolve).
export function createPaymentQrGate() {
  let saved = null;
  return {
    markSaved({ items, totals, snapshot, legacyImageUrl, phone }) {
      saved = {
        phoneDigits: digitsOf(phone),
        fingerprint: paymentFingerprint(items, totals),
        payment: resolvePaymentQr(snapshot, totals.net, legacyImageUrl)
      };
    },
    reset() {
      saved = null;
    },
    isSaved: () => saved !== null,
    // True once saved AND the live phone is still the one that was saved. Changing
    // the phone after a save shows the new number in full again until the next save.
    isPhoneSaved: (phone) => saved !== null && saved.phoneDigits === digitsOf(phone),
    // True while the live items/totals are exactly what the last save stored.
    isInSync: ({ items, totals }) => saved !== null && saved.fingerprint === paymentFingerprint(items, totals),
    resolve({ items, totals, enabled }) {
      if (!enabled || !saved || !saved.payment) return null;
      return saved.fingerprint === paymentFingerprint(items, totals) ? saved.payment : null;
    }
  };
}

function hasContent(doc) {
  const plate = doc.plate || {};
  const hasPlate = Boolean(String(plate.characters || '').trim() || String(plate.number || '').trim());
  const hasPhone = Boolean(String(doc.phone || '').replace(/\D/g, ''));
  const hasItems =
    Array.isArray(doc.items) && doc.items.some((item) => String(item.name || '').trim() || Number(item.price) !== 0);
  const hasOwner = Boolean(String(doc.ownerName || '').trim());
  return hasPlate || hasPhone || hasItems || hasOwner;
}

// Everything the customer sees is LIVE from `doc`: plate/province/phone, the item
// list with prices and the total, so each item can be checked the moment staff
// picks it. Only the payment QR and account (`payment`) wait for a save: they are
// shown while `saved` is true, i.e. the live document matches the last successful
// save. A blank form normally has nothing to show, so present idle unless the
// caller is deliberately claiming the display for a brand-new document.
export function buildView({ branch, doc, saved, payment, phoneSaved, forceActive = false }) {
  if (!forceActive && !hasContent(doc)) return sanitizeView({ kind: VIEW.IDLE, branch });
  const view = {
    kind: VIEW.ACTIVE,
    branch,
    doc: {
      docType: doc.docType,
      plate: doc.plate,
      // Full while staff are still typing it so the customer can verify it; the
      // last 3 digits are hidden once that number has been saved.
      phone: phoneSaved ? maskPhoneTail(doc.phone) : doc.phone,
      carType: doc.carType,
      ownerName: doc.ownerName,
      saved: Boolean(saved),
      items: doc.items,
      totals: doc.totals
    },
    payment: saved ? payment : null
  };
  return sanitizeView(view);
}
